import { useState, useEffect, useRef } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Send, Sparkles, Lock, ExternalLink, ChevronDown,
  AlertCircle, X, User, Bot,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  getClients, getSessions,
  type CoachingClient, type CoachingSession,
} from '../../lib/coaching-sessions';

interface OutletCtx { isUnlocked: boolean; }

const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

interface Message {
  role: 'coach' | 'ai';
  content: string;
  isDemo?: boolean;
}

const QUICK_PROMPTS = [
  { label: 'Analyse This Client', prompt: 'What are the most important patterns in this client\'s recent sessions?' },
  { label: 'Prepare Next Session', prompt: 'Create a next-session plan based on this client\'s latest report.' },
  { label: 'Generate Questions', prompt: 'Give me 10 powerful coaching questions for this client.' },
  { label: 'Identify Risks', prompt: 'What should I be careful not to push too quickly with this client?' },
  { label: 'Draft Follow-Up', prompt: 'Write a warm follow-up message based on the latest session.' },
  { label: 'Compare Sessions', prompt: 'What has shifted between this client\'s last two sessions?' },
];

const DEMO_PROMPTS = [
  { label: 'Patterns for Alex', prompt: 'What patterns are showing up with Alex?' },
  { label: 'Plan for Sarah', prompt: 'Create a next-session plan for Sarah.' },
  { label: 'Questions for David', prompt: 'Give me 10 questions for David.' },
  { label: 'Follow-up for Alex', prompt: 'Draft a follow-up message for Alex.' },
];

export function SICoachIntelligencePage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();
  const { user } = useAuth();

  const [coachId, setCoachId] = useState<string | null>(null);
  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const [selectedClientId, setSelectedClientId] = useState<string>('all');
  const [selectedSessionId, setSelectedSessionId] = useState<string>('latest');

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isUnlocked || !user) return;
    const load = async () => {
      setLoadingData(true);
      const { data: coach } = await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle();
      if (!coach) { setLoadingData(false); return; }
      setCoachId(coach.id);
      const [c, s] = await Promise.all([getClients(coach.id), getSessions(coach.id)]);
      setClients(c);
      setSessions(s);
      setLoadingData(false);
    };
    load();
  }, [isUnlocked, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const clientSessions = selectedClientId === 'all'
    ? sessions
    : sessions.filter(s => s.client_id === selectedClientId);
  const completedSessions = clientSessions.filter(s => s.analysis_status === 'complete');

  const buildContext = () => {
    if (!isUnlocked) return { isDemo: true };

    const client = selectedClient;
    const session = selectedSessionId === 'latest'
      ? completedSessions[0]
      : completedSessions.find(s => s.id === selectedSessionId);

    let sessionSummary = '';
    if (session?.session_analysis) {
      const a = session.session_analysis;
      const parts: string[] = [];
      if (a.session_summary?.overview) parts.push(`Summary: ${a.session_summary.overview}`);
      if (a.key_themes?.length) parts.push(`Key themes: ${a.key_themes.map(t => t.theme).join(', ')}`);
      if (a.breakthrough_zone?.area_of_deepest_leverage) parts.push(`Breakthrough zone: ${a.breakthrough_zone.area_of_deepest_leverage}`);
      if (a.limiting_beliefs?.length) parts.push(`Limiting beliefs: ${a.limiting_beliefs.map(b => b.possible_belief).join('; ')}`);
      if (a.suggested_next_session_focus) parts.push(`Suggested next focus: ${a.suggested_next_session_focus}`);
      if (a.important_cautions?.length) parts.push(`Cautions: ${a.important_cautions.join('; ')}`);
      sessionSummary = parts.join('\n');
    }

    return {
      isDemo: false,
      clientName: client?.full_name,
      sessionSummary: sessionSummary || undefined,
      contextType: selectedClientId === 'all' ? 'all_clients' : selectedSessionId === 'latest' ? 'specific_client' : 'specific_session',
    };
  };

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;

    const userMsg: Message = { role: 'coach', content: trimmed };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setThinking(true);
    setError('');

    const ctx = buildContext();

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('coach-intelligence-ai', {
        body: {
          messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
          ...ctx,
        },
      });

      if (fnErr) throw fnErr;
      if (!data?.reply) throw new Error('No reply received');

      setMessages(prev => [...prev, { role: 'ai', content: data.reply, isDemo: ctx.isDemo }]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const hasNoData = isUnlocked && !loadingData && completedSessions.length === 0 && clients.length === 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-success-10 rounded-2xl flex-shrink-0">
            <Brain className="w-6 h-6 text-success-50" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-80">Coach Intelligence AI</h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-10 text-success-50 text-xs font-semibold">
                <Sparkles className="w-3 h-3" />
                AI-Assisted
              </span>
            </div>
            <p className="text-sm text-gray-50 mt-1 leading-relaxed">
              Ask questions about your session reports, client patterns, breakthrough zones, and next-session plans.
              Coach Intelligence AI supports your professional judgement — it does not replace it.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Pre-unlock state */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-5"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-gray-10 rounded-xl flex-shrink-0">
              <Lock className="w-4 h-4 text-gray-40" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-80">Coach Intelligence AI Preview</h2>
              <p className="text-sm text-gray-50 mt-1 leading-relaxed">
                This is where you'll ask questions about your session reports, client patterns, breakthrough zones, and next-session plans.
                Your own Coach Intelligence AI will activate when your first Session Intelligence™ report is unlocked during the live walkthrough.
              </p>
              <p className="text-xs text-gray-40 mt-2">
                In the meantime, explore using sample client data below.
              </p>
            </div>
          </div>
          <a
            href={WALKTHROUGH_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            Join the Live Walkthrough to Unlock Your First Report
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      )}

      {/* No data yet (unlocked but no sessions) */}
      {hasNoData && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-6 text-center"
        >
          <div className="w-12 h-12 bg-success-10 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Brain className="w-6 h-6 text-success-50" />
          </div>
          <h2 className="text-base font-semibold text-gray-80 mb-2">Upload a session to get started</h2>
          <p className="text-sm text-gray-50 leading-relaxed mb-4">
            Once your first Session Intelligence™ report is generated, you can ask Coach Intelligence AI questions about the session, client patterns, and next-session planning.
          </p>
          <Link
            to="/si/app/reports"
            className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            Upload Your First Session
          </Link>
        </motion.div>
      )}

      {/* Context selector — only show when unlocked and has data */}
      {isUnlocked && !loadingData && (clients.length > 0 || completedSessions.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="bg-white rounded-2xl border border-gray-20 p-4"
        >
          <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-3">Context</p>
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-36">
              <label className="block text-xs text-gray-50 mb-1">Client</label>
              <div className="relative">
                <select
                  value={selectedClientId}
                  onChange={e => { setSelectedClientId(e.target.value); setSelectedSessionId('latest'); }}
                  className="w-full appearance-none px-3 py-2 pr-8 bg-brand-5 border border-gray-20 rounded-xl text-sm text-gray-80 focus:outline-none focus:border-success-40 transition-colors"
                >
                  <option value="all">All clients</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
              </div>
            </div>
            {selectedClientId !== 'all' && completedSessions.length > 0 && (
              <div className="flex-1 min-w-36">
                <label className="block text-xs text-gray-50 mb-1">Session Report</label>
                <div className="relative">
                  <select
                    value={selectedSessionId}
                    onChange={e => setSelectedSessionId(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 bg-brand-5 border border-gray-20 rounded-xl text-sm text-gray-80 focus:outline-none focus:border-success-40 transition-colors"
                  >
                    <option value="latest">Latest session</option>
                    {completedSessions.map(s => (
                      <option key={s.id} value={s.id}>
                        {new Date(s.session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Quick prompt chips */}
      {(!isUnlocked || !hasNoData) && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <p className="text-xs font-semibold text-gray-40 uppercase tracking-wide mb-2">
            {isUnlocked ? 'Quick Prompts' : 'Sample Prompts — Demo Preview'}
          </p>
          <div className="flex flex-wrap gap-2">
            {(isUnlocked ? QUICK_PROMPTS : DEMO_PROMPTS).map(p => (
              <button
                key={p.label}
                onClick={() => sendMessage(p.prompt)}
                disabled={thinking}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-20 hover:border-success-40 hover:bg-success-5 text-xs font-medium text-gray-60 hover:text-success-50 rounded-xl transition-all disabled:opacity-50"
              >
                <Sparkles className="w-3 h-3" />
                {p.label}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Chat area */}
      {(!isUnlocked || !hasNoData) && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-white rounded-2xl border border-gray-20 overflow-hidden flex flex-col"
          style={{ minHeight: '420px' }}
        >
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ maxHeight: '480px' }}>
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="w-12 h-12 bg-success-10 rounded-2xl flex items-center justify-center mb-3">
                  <Brain className="w-6 h-6 text-success-50" />
                </div>
                <p className="text-sm font-semibold text-gray-60 mb-1">
                  {isUnlocked ? 'Ask Coach Intelligence AI' : 'Ask about sample clients'}
                </p>
                <p className="text-xs text-gray-40 max-w-xs leading-relaxed">
                  {isUnlocked
                    ? 'Ask about client patterns, next-session planning, follow-up messages, coaching questions, and more.'
                    : 'Try a sample prompt above to see how Coach Intelligence AI works with Alex, Sarah, or David.'}
                </p>
              </div>
            )}
            <AnimatePresence initial={false}>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex gap-3 ${msg.role === 'coach' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'ai' && (
                    <div className="w-7 h-7 rounded-full bg-success-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-3.5 h-3.5 text-success-50" />
                    </div>
                  )}
                  <div className={`max-w-[80%] ${msg.role === 'coach' ? 'order-first' : ''}`}>
                    {msg.role === 'ai' && msg.isDemo && (
                      <p className="text-xs text-orange-60 font-semibold mb-1">Demo Preview Response</p>
                    )}
                    <div
                      className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        msg.role === 'coach'
                          ? 'bg-success-40 text-white rounded-tr-sm'
                          : 'bg-brand-5 border border-gray-20 text-gray-70 rounded-tl-sm'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                  {msg.role === 'coach' && (
                    <div className="w-7 h-7 rounded-full bg-gray-80 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            {thinking && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 justify-start"
              >
                <div className="w-7 h-7 rounded-full bg-success-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-3.5 h-3.5 text-success-50" />
                </div>
                <div className="px-4 py-3 bg-brand-5 border border-gray-20 rounded-2xl rounded-tl-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-success-40 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-success-40 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-success-40 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="px-5 py-3 bg-red-50 border-t border-red-100 flex items-center gap-2 text-sm text-red-700"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{error}</span>
                <button onClick={() => setError('')} className="flex-shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input */}
          <div className="border-t border-gray-10 p-4">
            <div className="flex gap-3 items-end">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isUnlocked
                    ? 'Ask Coach Intelligence AI about a client, session, pattern, or next-session plan…'
                    : 'Ask about Alex, Sarah, or David — sample demo data…'
                }
                rows={2}
                className="flex-1 px-4 py-2.5 bg-brand-5 border border-gray-20 rounded-xl text-sm text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors resize-none leading-relaxed"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || thinking}
                className="p-2.5 bg-success-40 hover:bg-success-50 disabled:bg-gray-20 text-white rounded-xl transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-40 mt-2 leading-relaxed">
              Coach Intelligence AI provides coaching hypotheses and suggested areas for exploration. It does not diagnose or replace professional judgement.
              {!isUnlocked && (
                <span className="text-orange-60 font-medium"> Responses are based on sample demo data.</span>
              )}
            </p>
          </div>
        </motion.div>
      )}

      {/* What it can do — shown for pre-unlock or empty state */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl border border-gray-20 p-6"
        >
          <h3 className="text-sm font-semibold text-gray-80 mb-4">What Coach Intelligence AI Can Help With</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { title: 'Client Pattern Exploration', desc: 'Ask what patterns are repeating, what limiting beliefs keep appearing, what emotional themes are recurring.' },
              { title: 'Session Report Exploration', desc: 'Summarise coaching opportunities, identify the deepest leverage point, explore the difference between surface and deeper patterns.' },
              { title: 'Next Session Planning', desc: 'Get a focus area, an opening question, a full session plan, and what to avoid pushing too quickly.' },
              { title: 'Coaching Question Generation', desc: 'Generate questions by category: Meta Model, limiting beliefs, identity, values, emotional exploration, breakthrough zone.' },
              { title: 'Follow-Up Message Drafting', desc: 'Draft warm follow-ups, short WhatsApp-style messages, professional email summaries, and reflection prompts.' },
              { title: 'Progress Comparison', desc: 'Compare sessions, identify what patterns are reducing, notice language shifts from fixed identity toward ownership.' },
            ].map(item => (
              <div key={item.title} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-success-40 mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-80">{item.title}</p>
                  <p className="text-xs text-gray-50 mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
